import React, { useEffect, useState } from 'react';
import {
  Row, Button,
} from 'reactstrap';
import { useMutation, useQuery } from '@apollo/react-hooks';
import {
  UPDATE_TEMPLATES_RANK,
  UPDATE_TEMPLATE_CATEGORIES,
  UPDATE_TEMPLATE,
  DELETE_TEMPLATE_CATEGORY,
} from '../../mutation/mutation';
import { CategoryCard, removeTypenamesInCategories } from './categories-components/CategoryCard';
import CategoryInfos from './categories-components/CategoryInfos';
import { GET_TEMPLATE_CATEGORIES, GET_TEMPLATES } from '../../query/query';

const TemplatesOrderCategoriesPage = () => {
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [templateCategoriesArray, setTemplateCategoriesArray] = useState([]);
  const [modifiedTemplatesList, setModifiedTemplatesList] = useState([]);
  const [updateTemplateCategories] = useMutation(UPDATE_TEMPLATE_CATEGORIES);
  const [
    updateTemplatesRank,
    { loading: loadingUpdateTemplatesRank },
  ] = useMutation(UPDATE_TEMPLATES_RANK);
  const [updateTemplate] = useMutation(UPDATE_TEMPLATE, {
    update: (cache) => {
      cache.writeQuery({
        query: GET_TEMPLATE_CATEGORIES,
        data: { savGetTemplateCategories: [...templateCategoriesArray] },
      });
    },
  });
  const {
    data: { savGetTemplates = [] } = {},
  } = useQuery(
    GET_TEMPLATES, { fetchPolicy: 'no-cache' },
  );
  const {
    data: { savGetTemplateCategories: templateCategories = [] } = {},
  } = useQuery(
    GET_TEMPLATE_CATEGORIES, { fetchPolicy: 'no-cache' },
  );
  const [deleteTemplateCategory] = useMutation(DELETE_TEMPLATE_CATEGORY);

  useEffect(() => {
    if (templateCategories.length > 0) {
      setTemplateCategoriesArray(templateCategories);
    }
  }, [templateCategories]);

  const cleanAndUpdateTemplateCategories = async () => {
    setTemplateCategoriesArray([...templateCategoriesArray]);

    await updateTemplateCategories({
      variables:
      {
        newTemplatesCategories: removeTypenamesInCategories(templateCategoriesArray),
      },
    });
  };

  const onChangeCategoryPosition = async ({ index, subIndex, newIndex }) => {
    const element = subIndex !== undefined
      ? templateCategoriesArray[index].subCategories[subIndex] : templateCategoriesArray[index];

    if (subIndex !== undefined) {
      templateCategoriesArray[index].subCategories.splice(subIndex, 1);
      templateCategoriesArray[index].subCategories.splice(newIndex, 0, element);
    } else {
      templateCategoriesArray.splice(index, 1);
      templateCategoriesArray.splice(newIndex, 0, element);
    }

    await cleanAndUpdateTemplateCategories();
  };

  const updateTemplateRankForCategory = ({ templateRanks = [], categoryId, templateIndex }) => {
    const foundCategoryRank = templateRanks.findIndex(
      (categoryRank) => categoryRank.id === categoryId,
    );

    if (foundCategoryRank !== -1) {
      // eslint-disable-next-line no-param-reassign
      templateRanks[foundCategoryRank].rank = templateIndex;
    } else {
      templateRanks.push({
        id: categoryId,
        rank: templateIndex,
      });
    }

    return templateRanks.map((rank) => ({
      id: rank.id,
      rank: rank.rank,
    }));
  };

  const updateCategoryTemplatesPosition = async ({ newList, categoryId }) => {
    const buffModifiedTemplates = modifiedTemplatesList;
    const newListWithRanks = newList.map((template, index) => ({
      _id: template._id,
      name: template.name,
      thumbnailEn: template.thumbnailEn,
      thumbnailFr: template.thumbnailFr,
      ranks: updateTemplateRankForCategory({
        templateRanks: template.ranks, categoryId, templateIndex: index,
      }),
    }));

    newListWithRanks.forEach((template) => {
      const templateAlreadyModified = buffModifiedTemplates.find(
        (oldModifiedTemplate) => template._id === oldModifiedTemplate._id,
      );
      if (templateAlreadyModified) templateAlreadyModified.ranks = template.ranks;
      else buffModifiedTemplates.push(template);
    });

    setModifiedTemplatesList([...buffModifiedTemplates]);
  };

  const saveCategoryTemplatesPosition = async () => {
    await updateTemplatesRank({
      variables:
      {
        templates: modifiedTemplatesList,
      },
    });
    setModifiedTemplatesList([]);
  };

  const onCreateCategory = async ({ masterCategoryIndex, newCategory }) => {
    const newCategoryCleaned = { ...newCategory };

    delete newCategoryCleaned.position;
    if (masterCategoryIndex !== undefined) {
      templateCategoriesArray[masterCategoryIndex].subCategories.splice(
        newCategory.position, 0, newCategoryCleaned,
      );
    } else {
      newCategoryCleaned.subCategories = [];
      templateCategoriesArray.splice(newCategory.position, 0, newCategoryCleaned);
    }
    await cleanAndUpdateTemplateCategories();
  };

  const onDeleteCategory = async ({
    masterCategory,
    categoryId,
  }) => {
    try {
      let categoryToDelete = masterCategory;

      if (masterCategory) {
        const subCategoryIndex = masterCategory.subCategories.findIndex(
          (subCategory) => subCategory._id === categoryId,
        );
        categoryToDelete = { ...masterCategory.subCategories[subCategoryIndex] };
        masterCategory.subCategories.splice(subCategoryIndex, 1);
      } else {
        const categoryIndex = templateCategoriesArray.findIndex(
          (category) => category._id === categoryId,
        );
        categoryToDelete = { ...templateCategoriesArray[categoryIndex] };
        templateCategoriesArray.splice(categoryIndex, 1);
      }
      setTemplateCategoriesArray([...templateCategoriesArray]);
      await deleteTemplateCategory({
        variables: {
          category: removeTypenamesInCategories(null, masterCategory || categoryToDelete),
          subCategory: masterCategory
            ? removeTypenamesInCategories(null, categoryToDelete)
            : undefined,
        },
      });
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      {(isAddingCategory) && (
        <Row style={styles.centeredRow}>
          <CategoryInfos
            categoriesArray={templateCategoriesArray}
            style={styles.card}
            modifying={isAddingCategory}
            onCancelModify={() => setIsAddingCategory(false)}
            onEndModify={async (newCategory) => {
              setIsAddingCategory(false);
              await onCreateCategory({
                newCategory,
              });
            }}
          />
        </Row>
      )}
      {templateCategoriesArray.map((category, index) => (
        <Row key={category._id}>
          <Row style={styles.centeredRow}>
            <CategoryCard
              categoriesArray={templateCategories}
              index={index}
              category={category}
              templatesList={savGetTemplates}
              onChangeCategoryPosition={
                (newIndex, subIndex) => onChangeCategoryPosition({ index, newIndex, subIndex })
              }
              onChangeTemplatePosition={updateCategoryTemplatesPosition}
              updateTemplate={updateTemplate}
              loadingUpdateTemplatesRank={loadingUpdateTemplatesRank}
              onCreateCategory={onCreateCategory}
              onDeleteCategory={onDeleteCategory}
            />
          </Row>
        </Row>
      ))}
      <Row style={styles.centeredRow}>
        <Button
          color="primary"
          type="button"
          onClick={() => {
            setIsAddingCategory(true);
            // eslint-disable-next-line no-undef
            window.scrollTo(0, 0);
          }}
        >
          Add a new Category
        </Button>
      </Row>
      <Button
        disabled={!(modifiedTemplatesList.length > 0)}
        color="primary"
        type="button"
        style={styles.saveButton}
        onClick={saveCategoryTemplatesPosition}
      >
        Save templates positions
      </Button>
    </div>
  );
};

const styles = {
  centeredRow: {
    justifyContent: 'center',
    width: '100%',
  },
  card: {
    backgroundColor: '#ededed',
    borderColor: 'grey',
    borderWidth: 1,
    padding: 10,
    borderRadius: 30,
    marginBottom: 10,
    width: 800,
    alignSelf: 'center',
  },
  saveButton: {
    position: 'fixed',
    bottom: 20,
    right: 20,
  },
};

export default TemplatesOrderCategoriesPage;



// WEBPACK FOOTER //
// ./src/components/templates-manager/template-order-categories.js